import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  CheckCircle, 
  XCircle, 
  DollarSign,
  Calendar,
  Filter,
  Search,
  BarChart3,
  Target,
  Award,
  Activity
} from 'lucide-react';
import BackButton from '../components/BackButton';

export default function MyPredictions({ activeFilter, onFilterChange }) {
  const { isLoggedIn } = useAuth();
  
  // 筛选状态（从Layout接收activeFilter，本地管理searchTerm）
  const [localFilter, setLocalFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // 根据Header筛选条件过滤预测数据
  const getFilteredPredictionsByHeaderFilter = (predictions) => {
    if (!activeFilter || activeFilter === 'Active') {
      return predictions; // Active显示所有
    }
    
    // 根据币种筛选预测
    return predictions.filter(prediction => {
      const market = prediction.market;
      switch (activeFilter) {
        case 'Major':
          return ['BTC/USD', 'ETH/USD'].includes(market);
        case 'Altcoins':
          return ['SOL/USD', 'ADA/USD', 'AVAX/USD'].includes(market);
        case 'Meme':
          return ['DOGE/USD', 'SHIB/USD'].includes(market);
        case 'DeFi':
          return ['UNI/USD', 'AAVE/USD', 'COMP/USD'].includes(market);
        default:
          return true;
      }
    });
  };

  // 模拟预测数据
  const [predictions] = useState([
    {
      id: 1,
      market: 'BTC/USD',
      direction: 'up',
      entryPrice: 42500,
      currentPrice: 43200,
      targetPrice: 45000,
      amount: 100,
      status: 'active',
      timeRemaining: '2h 15m',
      createdAt: '2024-01-15T10:30:00Z',
      pnl: 700,
      pnlPercentage: 1.65
    },
    {
      id: 2,
      market: 'ETH/USD',
      direction: 'down',
      entryPrice: 2800,
      currentPrice: 2650,
      targetPrice: 2500,
      amount: 250,
      status: 'won',
      timeRemaining: null,
      createdAt: '2024-01-14T14:20:00Z',
      pnl: 1250,
      pnlPercentage: 5.36
    },
    {
      id: 3,
      market: 'SOL/USD',
      direction: 'up',
      entryPrice: 95,
      currentPrice: 88,
      targetPrice: 110,
      amount: 150,
      status: 'lost',
      timeRemaining: null,
      createdAt: '2024-01-13T09:15:00Z',
      pnl: -150,
      pnlPercentage: -7.37
    },
    {
      id: 4,
      market: 'ADA/USD',
      direction: 'up',
      entryPrice: 0.45,
      currentPrice: 0.47,
      targetPrice: 0.52,
      amount: 300,
      status: 'active',
      timeRemaining: '5h 42m',
      createdAt: '2024-01-15T08:00:00Z',
      pnl: 60,
      pnlPercentage: 4.44
    }
  ]);

  // 统计数据
  const stats = {
    totalPredictions: predictions.length,
    activePredictions: predictions.filter(p => p.status === 'active').length,
    wonPredictions: predictions.filter(p => p.status === 'won').length,
    lostPredictions: predictions.filter(p => p.status === 'lost').length,
    totalPnL: predictions.reduce((sum, p) => sum + p.pnl, 0),
    winRate: predictions.filter(p => p.status !== 'active').length > 0 
      ? (predictions.filter(p => p.status === 'won').length / predictions.filter(p => p.status !== 'active').length * 100).toFixed(1)
      : 0
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-plypto-bg flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-gray-400">Please connect your wallet to view your predictions.</p>
        </div>
      </div>
    );
  }

  // 首先根据Header筛选条件过滤
  const headerFilteredPredictions = getFilteredPredictionsByHeaderFilter(predictions);
  
  // 然后根据本地筛选条件和搜索词过滤
  const filteredPredictions = headerFilteredPredictions.filter(prediction => {
    const matchesLocalFilter = localFilter === 'all' || prediction.status === localFilter;
    const matchesSearch = prediction.market.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesLocalFilter && matchesSearch;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <Clock className="w-4 h-4 text-blue-400" />;
      case 'won':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'lost':
        return <XCircle className="w-4 h-4 text-red-400" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-blue-400 bg-blue-400/10';
      case 'won':
        return 'text-green-400 bg-green-400/10';
      case 'lost':
        return 'text-red-400 bg-red-400/10';
      default:
        return 'text-gray-400 bg-gray-400/10';
    }
  };

  return (
    <div className="bg-plypto-bg min-h-screen">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <BackButton />
            <div className="h-6 w-px bg-white/20"></div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">My Predictions</h1>
                <p className="text-gray-400">Track your prediction history and performance</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 pb-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center space-x-3">
              <BarChart3 className="w-6 h-6 text-blue-400" />
              <div>
                <div className="text-2xl font-bold text-white">{stats.totalPredictions}</div>
                <div className="text-gray-400 text-sm">Total Predictions</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center space-x-3">
              <Clock className="w-6 h-6 text-orange-400" />
              <div>
                <div className="text-2xl font-bold text-white">{stats.activePredictions}</div>
                <div className="text-gray-400 text-sm">Active Predictions</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center space-x-3">
              <Target className="w-6 h-6 text-green-400" />
              <div>
                <div className="text-2xl font-bold text-white">{stats.winRate}%</div>
                <div className="text-gray-400 text-sm">Win Rate</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center space-x-3">
              <DollarSign className="w-6 h-6 text-purple-400" />
              <div>
                <div className={`text-2xl font-bold ${stats.totalPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  ${stats.totalPnL >= 0 ? '+' : ''}{stats.totalPnL}
                </div>
                <div className="text-gray-400 text-sm">Total P&L</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white/5 rounded-xl p-6 border border-white/10 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            {/* Filter Tabs */}
            <div className="flex items-center space-x-1">
              {[
                { key: 'all', label: 'All', icon: BarChart3 },
                { key: 'active', label: 'Active', icon: Clock },
                { key: 'won', label: 'Won', icon: CheckCircle },
                { key: 'lost', label: 'Lost', icon: XCircle }
              ].map(filter => (
                <button
                  key={filter.key}
                  onClick={() => setLocalFilter(filter.key)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    localFilter === filter.key
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <filter.icon className="w-4 h-4" />
                  <span>{filter.label}</span>
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search cryptos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Predictions List */}
        <div className="space-y-4">
          {filteredPredictions.length === 0 ? (
            <div className="bg-white/5 rounded-xl p-12 border border-white/10 text-center">
              <TrendingUp className="w-16 h-16 text-gray-400 mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold text-white mb-2">No Predictions Found</h3>
              <p className="text-gray-400">
                {searchTerm ? 'Try adjusting your search terms.' : 'Start making predictions to see them here.'}
              </p>
            </div>
          ) : (
            filteredPredictions.map(prediction => (
              <div key={prediction.id} className="bg-white/5 rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {/* Direction Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      prediction.direction === 'up' ? 'bg-green-500/20' : 'bg-red-500/20'
                    }`}>
                      {prediction.direction === 'up' ? (
                        <TrendingUp className="w-5 h-5 text-green-400" />
                      ) : (
                        <TrendingDown className="w-5 h-5 text-red-400" />
                      )}
                    </div>

                    {/* Prediction Info */}
                    <div>
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-semibold text-white">{prediction.market}</h3>
                        <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(prediction.status)}`}>
                          {getStatusIcon(prediction.status)}
                          <span className="capitalize">{prediction.status}</span>
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 mt-1 text-sm text-gray-400">
                        <span>Entry: ${prediction.entryPrice}</span>
                        <span>Current: ${prediction.currentPrice}</span>
                        <span>Target: ${prediction.targetPrice}</span>
                        {prediction.timeRemaining && (
                          <span className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>{prediction.timeRemaining}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* P&L and Amount */}
                  <div className="text-right">
                    <div className={`text-lg font-semibold ${prediction.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {prediction.pnl >= 0 ? '+' : ''}${prediction.pnl}
                    </div>
                    <div className={`text-sm ${prediction.pnlPercentage >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {prediction.pnlPercentage >= 0 ? '+' : ''}{prediction.pnlPercentage}%
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      Amount: ${prediction.amount}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
